/**
 * Protected Route Component for Re:Nav-96
 *
 * Wraps routes that require authentication. Redirects unauthenticated users
 * to the login page while preserving the intended destination for redirect
 * after successful login.
 *
 * @example
 * // In router configuration
 * {
 *   path: 'member',
 *   element: <ProtectedRoute><MemberLayout /></ProtectedRoute>,
 *   children: [
 *     { path: 'dashboard', element: <DashboardPage /> },
 *   ]
 * }
 *
 * @example
 * // Wrap individual routes
 * <Route
 *   path="/member/dashboard"
 *   element={
 *     <ProtectedRoute>
 *       <DashboardPage />
 *     </ProtectedRoute>
 *   }
 * />
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router';
import { useAuth } from '@/hooks/useAuth';

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
  /**
   * Optional children to render when authenticated.
   * If not provided, renders <Outlet /> for nested routes.
   */
  children?: ReactNode;

  /**
   * Optional required role for access.
   * If specified, user must have this role or higher.
   */
  requiredRole?: 'member' | 'group_admin' | 'super_admin';

  /**
   * Custom redirect path when not authenticated.
   * Defaults to '/login'.
   */
  redirectTo?: string;
}

/**
 * Role hierarchy for authorization checks
 */
const roleHierarchy: Record<string, number> = {
  member: 1,
  group_admin: 2,
  super_admin: 3,
};

/**
 * Loading spinner component displayed while checking auth state
 */
function LoadingSpinner() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <svg
          className="animate-spin h-10 w-10 text-primary"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p className="text-white/60 text-sm">Checking authentication...</p>
      </div>
    </div>
  );
}

/**
 * Protected route wrapper component
 *
 * Checks if user is authenticated before rendering protected content.
 * Shows loading spinner while auth state is being determined.
 * Redirects to login if not authenticated, preserving the intended destination.
 * Optionally checks for required role access.
 *
 * @param props - Component props
 * @returns Protected content or redirect to login
 *
 * @example
 * // Basic usage with nested routes
 * <ProtectedRoute>
 *   <Outlet />
 * </ProtectedRoute>
 *
 * @example
 * // With role requirement
 * <ProtectedRoute requiredRole="super_admin">
 *   <AdminDashboard />
 * </ProtectedRoute>
 *
 * @example
 * // With custom redirect
 * <ProtectedRoute redirectTo="/signup">
 *   <PremiumContent />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  requiredRole,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while auth state is being determined
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Pass the current location as state so we can redirect back after login
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Check role authorization if required
  if (requiredRole && user) {
    const userRoleLevel = roleHierarchy[user.role] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    if (userRoleLevel < requiredRoleLevel) {
      // User doesn't have sufficient permissions
      // Redirect to dashboard with insufficient permissions
      return (
        <Navigate
          to="/member/dashboard"
          state={{ error: 'insufficient_permissions' }}
          replace
        />
      );
    }
  }

  // Render children if provided, otherwise render Outlet for nested routes
  return <>{children ?? <Outlet />}</>;
}

export default ProtectedRoute;
